/* The scenario x strategy matrix — every number computed by the SAME
   engine that runs on the server, locally in the browser (Tier-0). */
import React from 'react';
import type { Strategy } from '@flourish/schema';
import type { HorizonScore } from '@flourish/schema';
import { I, arrowMeta, initials, pctText } from '../ui.js';
import { useStore, activeEntity, visibleScenarios, type AppState } from '../store.jsx';
import { cell, ivCell, overallSuccess, toWeights, interventionStats, type ScenarioId } from '../local.js';
import { api, type EntityView, type ScenarioView } from '../api.js';

function scnTone(s: ScenarioView): string {
  if (s.prob <= 0) return 'mute';
  if (['sw'].includes(s.id)) return 'good';
  if (['bau1', 'bau2', 'unlim', 'crop', 'delayed'].includes(s.id)) return 'bad';
  return 'accent';
}

function Tile({ h }: { h: HorizonScore }) {
  if (h.death) {
    return (
      <div className="tile death" title={`${h.years}-year horizon: does not survive`}>
        <span className="tf">{h.years}y</span>
        <span className="tsym"><I k="skull" /></span>
      </div>
    );
  }
  const a = arrowMeta(h.pct);
  return (
    <div className={`tile ${a.tone}`} title={`${h.years}-year horizon: ${pctText(h.pct)} vs today`}>
      <span className="tf">{h.years}y</span>
      <span className="tarrow" style={{ transform: `rotate(${a.deg}deg)` }}><I k="arrow" /></span>
      <span className="tpct mono">{pctText(h.pct)}</span>
    </div>
  );
}

function CellTiles({ entity, strategy, scenario, mini, iid }: {
  entity: EntityView; strategy: Strategy; scenario: ScenarioView; mini?: boolean; iid?: string;
}) {
  if (scenario.prob <= 0) return <div className="na">not&nbsp;modeled</div>;
  const r = iid
    ? ivCell(entity, strategy, iid, scenario.id as ScenarioId)
    : cell(entity, strategy, scenario.id as ScenarioId);
  return (
    <div className={`tile-grid ${mini ? 'tg-mini' : ''}`}>
      {r.horizons.map(h => <Tile key={h.years} h={h} />)}
    </div>
  );
}

function ScenarioHead({ s, col }: { s: ScenarioView; col: number }) {
  const { set, toast } = useStore();
  const patch = async (body: { focus?: boolean; hidden?: boolean }) => {
    await api.patchScenario(s.id, body);
    if (body.hidden) toast(`${s.name} hidden — see blind spots`);
  };
  const na = s.prob <= 0;
  return (
    <div className="scn-head" style={{ gridColumn: col, gridRow: 1 }}>
      <div className={`scn-card tone-${scnTone(s)} ${s.focus && !na ? 'focus' : ''} ${na ? 'na' : ''}`}>
        <span className="strip" />
        <div className="scn-top">
          {na ? <span className="scn-focus off" /> : (
            <button className="scn-focus" title={s.focus ? 'In focus — optimising for this world' : 'Add to focus'}
              onClick={() => void patch({ focus: !s.focus })}>
              <I k={s.focus ? 'check2' : 'square'} />
            </button>
          )}
          <div className="scn-prob mono" onClick={() => set({ drawer: { kind: 'scenario', scenarioId: s.id } })}>
            {s.prob}<small>%</small>
          </div>
          <button className="scn-hide" title="Hide — move to blind spots" onClick={() => void patch({ hidden: true })}>
            <I k="eyeOff" />
          </button>
        </div>
        <div className="scn-name" onClick={() => set({ drawer: { kind: 'scenario', scenarioId: s.id } })}>{s.name}</div>
        <div className="scn-cat" onClick={() => set({ drawer: { kind: 'scenario', scenarioId: s.id } })}>
          <span className="tag">{s.cat}</span><span className="scn-inf"><I k="info" /></span>
        </div>
        <div className="probbar"><i style={{ width: `${s.prob}%` }} /></div>
      </div>
    </div>
  );
}

function StrategyHead({ st, entity, row, expanded }: {
  st: Strategy; entity: EntityView; row: number; expanded: boolean;
}) {
  const { s, set, toast } = useStore();
  const stats = interventionStats(
    { ...entity }, st, toWeights(s.scenarios),
  );
  const pos = stats.pos, neg = stats.neg;
  const open = () => {
    if (st.isNoRegret) set({ drawer: { kind: 'no-regret', strategyId: st.id } });
    else if (st.isMostLikely) set({ drawer: { kind: 'most-likely', entityId: entity.id } });
    else set({ drawer: { kind: 'strategy-editor', strategyId: st.id } });
  };
  const del = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await api.deleteStrategy(st.id).catch((err: Error) => toast(err.message));
  };
  return (
    <div className={`strat-head ${expanded ? 'expanded' : ''}`} style={{ gridColumn: 1, gridRow: row }}>
      <div className={`strat-card ${st.isNoRegret ? 'noregret' : ''} ${expanded ? 'expanded' : ''}`} onClick={open}>
        <div className="strat-top">
          <button className={`expand-btn ${expanded ? 'on' : ''}`}
            title={expanded ? 'Hide interventions' : 'Show interventions'}
            onClick={(e) => { e.stopPropagation(); set({ expanded: { ...s.expanded, [st.id]: !expanded } }); }}>
            <I k={expanded ? 'chevD' : 'chevR'} />
          </button>
          <div className="strat-name"><span className="nm">{st.name}</span></div>
          {st.isNoRegret ? <span className="nr-badge"><I k="wand" /> auto</span>
            : st.isMostLikely ? <span className="nr-badge" title="Curated placeholder — real inference lands with the intelligence layer"><I k="globe" /> inferred</span>
            : (
              <div className="strat-actions">
                <button className="mini" title="Edit" onClick={(e) => { e.stopPropagation(); set({ drawer: { kind: 'strategy-editor', strategyId: st.id } }); }}><I k="edit" /></button>
                <button className="mini danger" title="Delete" onClick={del}><I k="trash" /></button>
              </div>
            )}
        </div>
        <div className="strat-metrics">
          <div className="sm-metric"><span className="v mono">{st.interventions.length}</span><span className="l"><I k="layers" />Actions</span></div>
          <div className="sm-metric pos"><span className="v mono">{pos}</span><span className="l"><I k="arrowUp" />Positive</span></div>
          <div className="sm-metric neg"><span className="v mono">{neg}</span><span className="l"><I k="arrowDown" />Negative</span></div>
        </div>
      </div>
    </div>
  );
}

function Rail({ s, col, rows }: { s: AppState; col: number; rows: number }) {
  const { set, toast } = useStore();
  const e = activeEntity(s)!;
  const weights = toWeights(s.scenarios);
  const others = s.entities.filter(x => x.id !== e.id);
  const nr = (s.strategiesByEntity[e.id] ?? []).find(x => x.isNoRegret);
  return (
    <div className="rail" style={{ gridColumn: col, gridRow: `1 / ${rows + 1}` }}>
      <div className="rail-inner">
        <div className="rail-active">
          <div className="ent-card is-active" onClick={() => set({ drawer: { kind: 'entity-editor', entityId: e.id } })}>
            <div className="ent-head">
              <span className="ent-avatar" style={{ background: e.accent }}>{initials(e.name)}</span>
              <div className="ent-meta"><div className="en">{e.name}</div><div className="et">{e.type}</div></div>
              <span className="ent-role">You</span>
            </div>
            <div className="ent-score">
              <span className="v mono" style={{ color: e.accent }}>{nr ? overallSuccess(e, nr, weights) : '—'}</span>
              <span className="l">no-regret success @ 50y</span>
            </div>
          </div>
        </div>
        <div className="rail-label">Competitors · most-likely play</div>
        {others.map(o => {
          const ml = (s.strategiesByEntity[o.id] ?? []).find(x => x.isMostLikely);
          return (
            <div key={o.id} className="ent-card" onClick={() => { set({ activeEntityId: o.id }); toast(`Now viewing ${o.name}`); }}>
              <div className="ent-head">
                <span className="ent-avatar" style={{ background: o.accent }}>{initials(o.name)}</span>
                <div className="ent-meta"><div className="en">{o.name}</div><div className="et">{o.type}</div></div>
              </div>
              <div className="ent-score">
                <span className="v mono">{ml ? overallSuccess(o, ml, weights) : '—'}</span>
                <span className="l">most-likely @ 50y</span>
              </div>
              <div className="ent-actions">
                <button className="btn icon sm" title={`View ${o.name}'s most-likely strategy`}
                  onClick={(ev) => { ev.stopPropagation(); set({ drawer: { kind: 'most-likely', entityId: o.id } }); }}>
                  <I k="layers" />
                </button>
              </div>
            </div>
          );
        })}
        <button className="add-tile" onClick={() => set({ drawer: { kind: 'entity-editor', entityId: null } })}>
          <I k="plus" /> Add entity
        </button>
      </div>
    </div>
  );
}

export function Matrix() {
  const { s, set } = useStore();
  const e = activeEntity(s);
  if (!e) return null;
  const strategies = s.strategiesByEntity[e.id] ?? [];
  const vis = visibleScenarios(s);
  const nCols = vis.length;

  const parts: React.ReactNode[] = [];

  parts.push(
    <div key="corner" className="corner cell" style={{ gridColumn: 1, gridRow: 1, border: 0 }}>
      <div className="legend-card">
        <div className="lbl"><I k="clock" /> How to read a cell</div>
        <div className="legend-grid">
          <div className="tile up"><span className="tf">5y</span><span className="tarrow" style={{ transform: 'rotate(-42deg)' }}><I k="arrow" /></span><span className="tpct mono">+6%</span></div>
          <div className="tile down-soft"><span className="tf">15y</span><span className="tarrow" style={{ transform: 'rotate(22deg)' }}><I k="arrow" /></span><span className="tpct mono">-3%</span></div>
          <div className="tile down"><span className="tf">30y</span><span className="tarrow" style={{ transform: 'rotate(58deg)' }}><I k="arrow" /></span><span className="tpct mono">-24%</span></div>
          <div className="tile death"><span className="tf">50y</span><span className="tsym"><I k="skull" /></span></div>
        </div>
        <div className="legend-note">Projected success vs <b>today</b> at 5 / 15 / 30 / 50 yrs. <I k="skull" /> = doesn't survive. Click any cell for the <b>why</b>.</div>
      </div>
    </div>,
  );

  vis.forEach((scn, i) => parts.push(<ScenarioHead key={`h_${scn.id}`} s={scn} col={i + 2} />));

  let row = 2;
  for (const st of strategies) {
    const expanded = !!s.expanded[st.id];
    parts.push(<StrategyHead key={`s_${st.id}`} st={st} entity={e} row={row} expanded={expanded} />);
    vis.forEach((scn, i) => {
      parts.push(
        <div key={`c_${st.id}_${scn.id}`}
          className={`cellcol cell ${expanded ? 'grp' : ''} ${scn.prob <= 0 ? 'na' : ''}`}
          style={{ gridColumn: i + 2, gridRow: row }}
          onClick={scn.prob > 0 ? () => set({ drawer: { kind: 'cell', strategyId: st.id, scenarioId: scn.id } }) : undefined}>
          <CellTiles entity={e} strategy={st} scenario={scn} />
        </div>,
      );
    });
    row++;

    if (expanded) {
      const stats = interventionStats(e, st, toWeights(s.scenarios));
      st.interventions.forEach((iv, idx) => {
        const last = idx === st.interventions.length - 1;
        const net = stats.detail.find(d => d.iid === iv.iid)?.net ?? 0;
        parts.push(
          <div key={`ivh_${iv.iid}`} className={`iv-head ${last ? 'iv-last' : ''}`} style={{ gridColumn: 1, gridRow: row }}>
            <div className="iv-card" onClick={() => !st.isMostLikely && set({ drawer: { kind: 'strategy-editor', strategyId: st.id } })}>
              <span className={`iv-dir ${net >= 0 ? 'pos' : 'neg'}`}><I k={net >= 0 ? 'arrowUp' : 'arrowDown'} /></span>
              <div className="iv-main">
                <div className="iv-name">{iv.name}</div>
                <div className="iv-meta">
                  <span className="iv-owner"><I k="owner" />{iv.owner || '—'}</span>
                  <span className={`prio p-${iv.priority}`}>{iv.priority}</span>
                </div>
              </div>
            </div>
          </div>,
        );
        vis.forEach((scn, i) => {
          parts.push(
            <div key={`ivc_${iv.iid}_${scn.id}`}
              className={`cellcol cell iv grp ${last ? 'grp-end' : ''} ${scn.prob <= 0 ? 'na' : ''}`}
              style={{ gridColumn: i + 2, gridRow: row }}
              onClick={scn.prob > 0 ? () => set({ drawer: { kind: 'iv-cell', strategyId: st.id, iid: iv.iid, scenarioId: scn.id } }) : undefined}>
              <CellTiles entity={e} strategy={st} scenario={scn} mini iid={iv.iid} />
            </div>,
          );
        });
        row++;
      });
    }
  }

  parts.push(
    <div key="add" className="add-strat-row" style={{ gridColumn: 1, gridRow: row }}>
      <button className="add-tile" onClick={() => set({ drawer: { kind: 'strategy-editor', strategyId: null } })}>
        <I k="plus" /> Add strategy
      </button>
    </div>,
  );

  parts.push(<Rail key="rail" s={s} col={nCols + 2} rows={row} />);

  return (
    <main className="matrix-scroll" id="matrixScroll">
      <div className="matrix" id="matrix"
        style={{ gridTemplateColumns: `var(--rowhead) repeat(${nCols}, var(--col)) var(--entitycol)` }}>
        {parts}
      </div>
    </main>
  );
}
